import type { JSX, ReactNode } from 'react';
import { Group, Text } from '@mantine/core';
import { useCallback, useState } from 'react';
import { getDisplayString, getReferenceString } from '@medplum/core';
import type { OperationOutcome, Patient, Resource, ResourceType } from '@medplum/fhirtypes';
import {
  useMedplum,
  ResourceAvatar,
  AsyncAutocomplete,
  AsyncAutocompleteProps,
  AsyncAutocompleteOption,
} from '@medplum/react';
import useResources from '@/hooks/useResources';

/**
 * RT is a readonly tuple/array of resourceType string literals.
 * Example: ['Patient', 'Practitioner'] as const
 */
interface MultipleResourcesSelectorProps<T extends Resource> {
  readonly resourceTypes: T['resourceType'][];
  readonly name: string;
  readonly defaultValue?: T[];
  readonly searchCriteria?: {
    [K in T['resourceType']]?: Record<string, string>;
  };
  readonly placeholder?: string;
  readonly required?: boolean;
  readonly itemComponent?: (props: AsyncAutocompleteOption<T>) => JSX.Element | ReactNode;
  readonly onChange?: (value: T[]) => void;
  readonly disabled?: boolean;
  readonly toOption?: AsyncAutocompleteProps<T>['toOption'];
  readonly label?: AsyncAutocompleteProps<T>['label'];
  readonly error?: AsyncAutocompleteProps<T>['error'];
}

/**
 * Search parameter overrides for specific resource types.
 * Use this to specify the search parameter to use for a given resource type.
 * Otherwise it will fallback to "name" if the resource type is in NAME_RESOURCE_TYPES.
 * Otherwise it will fallback to "_id".
 */
const SEARCH_CODES: Record<string, string> = {
  Device: 'device-name',
  Observation: 'code',
  Subscription: 'criteria',
  User: 'email:contains',
  PractitionerRole: 'practitioner.name',
};

/**
 * Resource types that should use the "name" search parameter.
 * This is the full list of resource types that have a "name" search parameter.
 * Otherwise it will fallback to "_id".
 */
const NAME_RESOURCE_TYPES = [
  'AccessPolicy',
  'Account',
  'ActivityDefinition',
  'Bot',
  'CapabilityStatement',
  'CareTeam',
  'ClientApplication',
  'CodeSystem',
  'CompartmentDefinition',
  'ConceptMap',
  'EffectEvidenceSynthesis',
  'Endpoint',
  'EventDefinition',
  'Evidence',
  'EvidenceVariable',
  'ExampleScenario',
  'GraphDefinition',
  'Group',
  'HealthcareService',
  'ImplementationGuide',
  'InsurancePlan',
  'Library',
  'Location',
  'Measure',
  'MedicinalProduct',
  'MessageDefinition',
  'NamingSystem',
  'OperationDefinition',
  'Organization',
  'Patient',
  'Person',
  'PlanDefinition',
  'Practitioner',
  'Project',
  'Questionnaire',
  'RelatedPerson',
  'ResearchDefinition',
  'ResearchElementDefinition',
  'ResearchStudy',
  'RiskEvidenceSynthesis',
  'SearchParameter',
  'StructureDefinition',
  'StructureMap',
  'TerminologyCapabilities',
  'TestScript',
  'UserConfiguration',
  'ValueSet',
];

/**
 * Returns the search parameter to use for the given resource type.
 * If the resource type is in SEARCH_CODES, then that value is used.
 * Otherwise, if the resource type is in NAME_RESOURCE_TYPES, then "name" is used.
 * Otherwise, "_id" is used.
 * @param resourceType - The FHIR resource type.
 * @returns The search parameter to use for the autocomplete input.
 */
function getSearchParamForResourceType(resourceType: string): string {
  return SEARCH_CODES[resourceType] ?? (NAME_RESOURCE_TYPES.includes(resourceType) ? 'name' : '_id');
}

function toOption<T extends Resource>(resource: T): AsyncAutocompleteOption<T> {
  return {
    value: getReferenceString(resource) ?? '',
    label: getDisplayString(resource),
    resource,
  };
}

const DefaultItemComponent = ({ label, resource, active: _active, ...others }: AsyncAutocompleteOption<any>) => {
  return (
    <div {...others}>
      <Group wrap="nowrap">
        <ResourceAvatar value={resource} />
        <div>
          <Text>{label}</Text>
          <Text size="xs" c="dimmed">
            {(resource as Patient).birthDate || resource.id}
          </Text>
        </div>
      </Group>
    </div>
  );
};

export function MultipleResourcesSelector<T extends Resource>(
  props: MultipleResourcesSelectorProps<T>
): JSX.Element | null {
  const medplum = useMedplum();
  const { resourceTypes, searchCriteria } = props;
  const [outcome, setOutcome] = useState<OperationOutcome>();
  const defaultValue = useResources<T>(props.defaultValue ?? [], setOutcome);
  const ItemComponent = (props.itemComponent ?? DefaultItemComponent) as (
    p: AsyncAutocompleteOption<T>
  ) => JSX.Element | ReactNode;
  const onChange = props.onChange;

  const loadValues = useCallback(
    async (input: string, signal: AbortSignal): Promise<T[]> => {
      // Fire parallel searches for each resource type and flatten results
      const promises = resourceTypes.map((rt: T['resourceType']) => {
        const searchCriteriaForResourceType = searchCriteria?.[rt] ?? {};

        const searchCode = getSearchParamForResourceType(rt);
        const searchParams = new URLSearchParams({
          [searchCode]: input ?? '',
          _count: '10',
          ...searchCriteriaForResourceType,
        });

        return medplum.searchResources(rt as ResourceType, searchParams, { signal });
      });

      const results = await Promise.all(promises);
      return results.flat() as T[];
    },
    [medplum, resourceTypes, searchCriteria]
  );

  const handleChange = useCallback(
    (newResources: T[]) => {
      if (onChange) {
        onChange(newResources);
      }
    },
    [onChange]
  );

  const isLoadingDefaults = (props.defaultValue?.length ?? 0) > 0 && defaultValue === undefined && !outcome;
  if (isLoadingDefaults) {
    return null;
  }

  const defaultValueKey = (Array.isArray(defaultValue) ? defaultValue : [defaultValue])
    .map((r) => getReferenceString(r as T))
    .join(',');

  return (
    <AsyncAutocomplete<T>
      key={defaultValueKey}
      disabled={props.disabled}
      name={props.name}
      label={props.label}
      error={props.error}
      required={props.required}
      itemComponent={ItemComponent}
      defaultValue={defaultValue as T[] | undefined}
      placeholder={props.placeholder}
      toOption={props.toOption ?? toOption}
      loadOptions={loadValues}
      onChange={handleChange}
      clearable
    />
  );
}
